import { useState } from 'react';
import { CustomerProfileEndpoint, CustomerRegisterEndpoint, ForgotPasswordEndpoint, LoginEndpoint, LogoutEndpoint, PasswordResetEndpoint } from '../../common/endpoints';
import { postRequest } from '../../api/requests';
import useAuthStore from '../../datastore/useAuth';
import { Customer, ServerResponse, Station, Staff } from '../../common/types';
import { UserType } from '../../common/localStorageSvc';
import { updateStation } from '../../api/staff';

export const useAuthApi = () => {
    const { setUserData, clearUserData, currentUser } = useAuthStore(state => ({
        setUserData: state.setUserData,
        clearUserData: state.clearUserData,
        currentUser: state.currentUser
    }));

    const [loading, setLoading] = useState(false);

    const login = async (email: string, password: string): Promise<UserType | null> => {
        setLoading(true);
        try {
            const result = await postRequest(LoginEndpoint, { user: email, password });
            if ((result.status === 200 || result.status === 201) && result.data) {
                setUserData(result.data);
                return result.data;
            } else if (result.status === 400) {
                throw new Error('Email or password is incorrect');
            } else {
                console.error(result.data || 'An error occurred');
                throw new Error(result.data?.message || 'An error occurred');
            }
        } finally {
            setLoading(false);
        }
    };

    const logout = async (): Promise<void> => {
        setLoading(true);
        try {
            await postRequest(LogoutEndpoint, {});
            clearUserData();
        } finally {
            setLoading(false);
        }
    };

    const forgotPassword = async (email: string): Promise<void> => {
        setLoading(true);
        try {
            await postRequest(ForgotPasswordEndpoint, { email });
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const resetPassword = async (email: string, password: string, token: string): Promise<void> => {
        setLoading(true);
        try {
            const result = await postRequest(PasswordResetEndpoint, { email, password, token });
            if (result.status !== 200 && result.status !== 201) {
                console.error(result.data || 'An error occurred');
                throw new Error(result.data || 'An error occurred');
            }
        } finally {
            setLoading(false);
        }
    };

    const register = async (email: string, password: string, name: string, phone: string): Promise<Customer | null> => {
        setLoading(true);
        try {
            const results: ServerResponse<Customer> = await postRequest(CustomerRegisterEndpoint, { email, password, name, phone });
            if ((results.status === 200 || results.status === 201) && results.data) {
                const clone = { ...results.data };
                setUserData(clone);
                return clone;
            } else {
                throw new Error('Registration failed');
            }
        } catch (error) {
            console.error(error);
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const updateCustomerProfile = async (name: string, phoneNumber: string): Promise<Customer | null> => {
        setLoading(true);
        try {
            const results: ServerResponse<Customer> = await postRequest(`${CustomerProfileEndpoint}`, { name, phone: phoneNumber });
            if (results.status === 200 || results.status === 201) {
                const clone = results.data as Customer;
                setUserData(clone);
                return clone;
            } else {
                throw new Error('Profile update failed');
            }
        } catch (error) {
            console.error(error);
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const updateStaffStation = async (station: Station): Promise<void> => {
        setLoading(true);
        try {
            const staffUser = currentUser as Staff;
            const result = await updateStation(staffUser?.email, station.id);
            if (result.status === 200 || result.status === 201) {
                const updatedUser = {
                    ...staffUser,
                    stationId: station.id,
                    stationName: station.name,
                };
                setUserData(updatedUser);
            } else {
                throw new Error('Update station failed');
            }
        } finally {
            setLoading(false);
        }
    };

    return {
        login,
        logout,
        forgotPassword,
        resetPassword,
        register,
        updateCustomerProfile,
        updateStaffStation,
        loading
    };
};
